import cn from 'classnames';
// React
import { useState, useMemo } from 'react';
// Hooks
import { useGetCurrUser } from 'hooks';
// Helpers
import { formatLargeNumber } from 'helpers/formatLargeNumber';
import { convertHtmlBioToPlainText } from 'helpers/profileBio';
import { truncateTextAtWord } from 'helpers/string';
import { combineSocialMediaLinks } from 'helpers/socialMedia';
import { withCDN } from 'helpers/cdn';
import {
  PROFILE_AVATAR_SIZES,
  PROFILE_AVATAR_WIDTHS,
} from 'helpers/profileAvatar';
// Types
import type { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { UserRole } from 'api/graphql-global-types';
// Ui
import Image from 'ui3/Image/Image';
// Ui3
import Text from 'ui3/Text/Text';
import AthleteMenu from 'components/Athlete/Description/components/AthleteMenu/AthleteMenu';
import TabsNavigation from '../TabsNavigation/TabsNavigation';
import AboutModal from './components/AboutModal/AboutModal';
// Components
import ViewEditShareProfileButtons from 'components/common3/ViewEditShareProfileButtons/ViewEditShareProfileButtons';
// Styles
import styles from './Description.module.scss';

export type MenuOption = {
  label: string;
  href: string;
};

type DescriptionProps = {
  store: GetStoreBySlug_store;
  isAthletePending?: boolean;
  hasActiveLiveStream?: boolean;
};

const Description = ({
  store,
  isAthletePending = false,
  hasActiveLiveStream = false,
}: DescriptionProps) => {
  const { data } = useGetCurrUser();
  const { storeDetails, slug } = store;

  const isOwnProfile = data?.me?.slug === slug;

  const storeName = storeDetails?.storeName || '';
  const isLongerName = storeName.length > 20;
  const isLongestName = storeName.length >= 30;

  const avatarUrl = storeDetails?.avatarURL || '';

  const audience = store.audience ?? store.followerCount ?? 0;

  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  const plainBioText = convertHtmlBioToPlainText(
    storeDetails?.bio || ''
  ).trim();
  const isFanUser = store.role === UserRole.Customer;

  // Combine social media links from both sources
  const combinedSocialMedia = useMemo(
    () => combineSocialMediaLinks(store.socialMedia || [], store.storeDetails),
    [store.socialMedia, store.storeDetails]
  );

  const hasBioOrSocialMedia = !!plainBioText || combinedSocialMedia.length > 0;
  const shouldShowMoreLink = !isFanUser && hasBioOrSocialMedia;

  const bioPreview = plainBioText
    ? truncateTextAtWord(plainBioText, 60)
    : shouldShowMoreLink
    ? 'More about this profile...'
    : '';

  const handleOpenAboutModal = () => {
    setIsAboutModalOpen(true);
  };

  const handleCloseAboutModal = () => {
    setIsAboutModalOpen(false);
  };

  return (
    <div>
      <div className={styles.profileHeader}>
        <div className={styles.athleteInfo}>
          <div className={styles.imageWrapper}>
            {/* Same CDN URL as og:image and the Person schema, so Google picks the avatar as the thumbnail. */}
            <Image
              className={styles.image}
              src={avatarUrl || withCDN('/images/icons/avatar-placeholder.svg')}
              alt={storeName}
              title={storeName ? `${storeName} on MILLIONS` : undefined}
              priority
              ssrFill
              ssrWidths={PROFILE_AVATAR_WIDTHS}
              sizes={PROFILE_AVATAR_SIZES}
            />
          </div>

          <div className={styles.athleteName}>
            <Text
              as="h1"
              variant="h4"
              className={cn(styles.name, {
                [styles.longerName]: isLongerName,
                [styles.longestName]: isLongestName,
              })}
            >
              {storeName}
            </Text>
            <div className={styles.handleWrapper}>
              <Text variant="body1Medium16" className={styles.handle}>
                {`@${store.slug}`}
              </Text>
              <span className={styles.separator}>•</span>
              <span className={styles.audienceCount}>
                <span className={styles.audienceNumber}>
                  {formatLargeNumber(audience)}
                </span>
                <span className={styles.audienceLabel}> Audience</span>
              </span>
            </div>
            {shouldShowMoreLink && (
              <div className={styles.bioWrapper}>
                <Text
                  variant="body1Regular16"
                  color="lights-low"
                  className={styles.bioPreview}
                >
                  {bioPreview}
                  <button
                    type="button"
                    className={styles.moreLink}
                    onClick={handleOpenAboutModal}
                    aria-label={`Read more about ${storeName}`}
                  >
                    more
                  </button>
                </Text>
              </div>
            )}
          </div>
        </div>
        {isOwnProfile ? (
          <ViewEditShareProfileButtons showEditButton />
        ) : (
          <AthleteMenu store={store} className={styles.actionButtons} />
        )}
      </div>
      <TabsNavigation
        store={store}
        isAthletePending={isAthletePending}
        hasActiveLiveStream={hasActiveLiveStream}
      />

      {shouldShowMoreLink && (
        <AboutModal
          open={isAboutModalOpen}
          onClose={handleCloseAboutModal}
          storeName={storeName}
          bioText={storeDetails?.bio || ''}
          hashtags={store.hashtags}
          links={combinedSocialMedia}
          slug={store.slug || ''}
        />
      )}
    </div>
  );
};

export default Description;
